import React from 'react';
import { TrendingUp, BookOpen, AlertCircle, CheckCircle } from 'lucide-react';
import PromptBox from './PromptBox';
import { useNavigate } from 'react-router-dom';
import { trackQuerySubmit } from '../utils/analytics';

import SeoMeta from './SeoMeta';
const SAMPLE_PROMPTS = [
  'Analyze NIFTY 50 trend this week',
  'Compare TCS vs Infosys fundamentals',
  'Is RELIANCE fairly valued at current levels?',
  'HDFC Bank chart & key levels',
];

const AskPage: React.FC = () => {
  const navigate = useNavigate();

  const handlePrompt = (p: string) => {
    const ticker = p.match(/[A-Z]{2,}/)?.[0] ?? p.slice(0, 20);
    trackQuerySubmit(ticker);
    navigate(`/results?prompt=${encodeURIComponent(p)}`);
  };

  return (
    <>
      <SeoMeta title="Ask AI About Any Indian Stock | IndianStockMarketGPT" description="Ask a question about any NSE or BSE stock and get an educational, plain-English research summary. Not investment advice." url="https://indianstockmarketgpt.com/ask" />
    <div className="relative min-h-[82vh] flex flex-col items-center justify-center text-center px-4 py-20 overflow-hidden bg-white">
      {/* Subtle grid bg */}
      <div className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, rgba(27,79,216,0.03) 0 1px, transparent 1px 40px), repeating-linear-gradient(90deg, rgba(27,79,216,0.03) 0 1px, transparent 1px 40px)',
        }}
      />

      {/* Live badge */}
      <div className="relative inline-flex items-center gap-2 border border-slate-200 bg-white rounded-full px-4 py-1.5 text-[11px] font-semibold text-navy-500 uppercase tracking-widest mb-8 shadow-sm">
        <span className="relative flex h-1.5 w-1.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
        </span>
        Live NSE &amp; BSE Data
      </div>

      {/* Headline */}
      <h1 className="relative font-serif text-4xl md:text-6xl font-bold text-slate-900 max-w-3xl leading-tight mb-5">
        Ask anything about<br />
        <span className="text-navy-500">Indian stock markets</span>
      </h1>

      <p className="relative text-slate-500 text-base md:text-lg max-w-xl mb-8 leading-relaxed">
        Real-time quotes · AI analysis · Charts &amp; fundamentals<br />
        No brokerage account or login required.
      </p>

      {/* Sample chips */}
      <div className="relative flex flex-wrap justify-center gap-2 mb-6">
        {SAMPLE_PROMPTS.map(p => (
          <button
            key={p}
            onClick={() => handlePrompt(p)}
            className="text-[11px] px-3 py-1.5 rounded-full border border-slate-200 bg-white text-slate-500 hover:border-navy-300 hover:text-navy-600 hover:bg-navy-50 transition-colors shadow-sm"
          >
            {p}
          </button>
        ))}
      </div>

      {/* Prompt box */}
      <div className="relative w-full">
        <PromptBox onSubmit={handlePrompt} />
      </div>

      {/* Data sources */}
      <div className="relative mt-10 flex flex-wrap justify-center gap-4 text-[11px] text-slate-400">
        <span className="flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-navy-500" /> Yahoo Finance</span>
        <span className="text-slate-300">·</span>
        <span>NSE India</span>
        <span className="text-slate-300">·</span>
        <span>NIFTY 50 · SENSEX · BANK NIFTY</span>
      </div>
    </div>

    {/* Guide section */}
    <section className="bg-slate-50 border-t border-slate-100 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-2 mb-8">
          <BookOpen className="w-5 h-5 text-navy-500" />
          <h2 className="text-lg font-semibold text-slate-800">How to use IndianStockMarketGPT</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
              <h3 className="text-sm font-semibold text-slate-800">Questions that work well</h3>
            </div>
            <ul className="space-y-2.5 text-sm text-slate-600">
              {[
                'Analyse RELIANCE Industries fundamentals',
                'What is the RSI and moving average trend for TCS?',
                'Compare HDFC Bank vs ICICI Bank on key ratios',
                'What happened to BAJFINANCE stock in the last quarter?',
                'Explain the promoter holding trend for ADANIPORTS',
                'What sectors are outperforming NIFTY 50 this month?',
              ].map(q => (
                <li key={q} className="flex items-start gap-2">
                  <span className="text-navy-500 shrink-0 mt-0.5">→</span>
                  <span>{q}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
              <h3 className="text-sm font-semibold text-slate-800">What the AI does not do</h3>
            </div>
            <ul className="space-y-2.5 text-sm text-slate-600">
              {[
                'It does not recommend buying or selling any stock',
                'It does not predict future prices or returns',
                'It is not a SEBI-registered research analyst',
                'It does not provide personalised portfolio advice',
                'It does not access your brokerage account',
                'It does not guarantee accuracy of real-time data',
              ].map(q => (
                <li key={q} className="flex items-start gap-2">
                  <span className="text-amber-500 shrink-0 mt-0.5">✕</span>
                  <span>{q}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">What you get in each research brief</h3>
          <p className="text-sm text-slate-600 leading-relaxed mb-4">
            Every query returns a structured educational brief covering: current price and recent price action, key technical indicators (RSI, MACD, moving averages), fundamental snapshot (P/E, EPS, revenue growth, debt-to-equity), FII/DII activity and promoter shareholding trends, sector context, and any significant recent news. All of it in plain English — no finance jargon required to understand the output.
          </p>
          <p className="text-xs text-slate-400 leading-relaxed">
            <strong className="text-slate-500">Educational only.</strong> Not investment advice. Not SEBI registered. Always consult a SEBI-registered investment adviser before making financial decisions. Community opinion only.
          </p>
        </div>
      </div>
    </section>
    </>
  );
};

export default AskPage;
